import { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { Image, Flex } from "uikit-react";
import { useCookies } from "react-cookie";
import UIkit from "uikit";

import { AppContexts } from "../../../providers";
import RecordsPerPage from "../../common/RecordsPerPage";
import Pagination from "../../common/Pagination";
import ChecklistAdd from "./add";
import { DeleteDialog } from "../../common/Operations";
import { usePermissionsStore } from "../../permissions/permissions_store";

export const TASK_CATEGORY_OPTIONS = [
  { label: "All Types", value: "all" },
  { label: "Cleaning", value: "cleaning" },
  { label: "Inspection", value: "inspection" },
  { label: "Maintenance", value: "maintenance" },
  { label: "Other", value: "other" },
];

const DEFAULT_RPP = 10;

const labelForTypeValue = (value) =>
  TASK_CATEGORY_OPTIONS.find((o) => o.value === value)?.label || "—";

const Checklists = () => {
  const { get, settings } = useContext(AppContexts.SettingsContext);
  const { canEdit } = usePermissionsStore();
  const canManageChecklists = canEdit("operations.checklists");
  const {
    loading,
    error,
    checklists,
    checklistsCount,
    pagination,
    getChecklists,
    removeChecklist,
    cloneChecklist,
    setPage,
  } = useContext(AppContexts.ChecklistsContext);
  
  const [cookies] = useCookies(["records-per-page"]);
  const recordsPerPage = Number(cookies["records-per-page"] || DEFAULT_RPP) || DEFAULT_RPP;

  useEffect(() => {
    document.title = "Checklists - RueBaRue";
  }, []);

  const [criteria, setCriteria] = useState({
    search_term: "",
    type: "all",
  });

  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [addRequestKey, setAddRequestKey] = useState(0);
  const [checklistToDelete, setChecklistToDelete] = useState(null);

  // Fetch checklists from API
  useEffect(() => {
    fetchChecklists();
  }, [pagination.page, recordsPerPage, criteria.search_term, criteria.type]);

  const fetchChecklists = async () => {
    const params = {
      per_page: recordsPerPage,
      page: pagination.page,
      search_term: criteria.search_term,
    };

    // Only include type if it's not "all"
    if (criteria.type !== "all") {
      params.type = criteria.type;
    }
    
    try {
      await getChecklists(params);
    } catch (err) {
      console.error("Error fetching checklists:", err);
    }
  };

  const handleEdit = useCallback((template) => {
    setSelectedTemplate(template);
  }, []);

  const handleAdd = useCallback(() => {
    setSelectedTemplate(null);
    setAddRequestKey((prev) => prev + 1);
  }, []);

  const handleClone = useCallback(async (template) => {
    try {
      await cloneChecklist(template.id);
    } catch (err) {
      console.error("Error cloning checklist:", err);
    }
  }, [cloneChecklist]);

  const handleDelete = useCallback(
    (template) => {
      setChecklistToDelete(template);
    },
    []
  );

  const confirmDelete = useCallback(
    async (checklist) => {
      try {
        await removeChecklist(checklist.id);
        UIkit.modal("#modal-checklist-delete").hide();
      } catch (err) {
        console.error("Error deleting checklist:", err);
      }
    },
    [removeChecklist]
  );

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((prev) => ({
      ...prev,
      [key]: value,
    }));
    // Reset to page 0 when filters change
    if (key !== 'page') {
      setPage(0);
    }
  }, [setPage]);

  const handleIndexChanged = useCallback((newPageIndex) => {
    setPage(newPageIndex);
  }, [setPage]);

  const totalPages = Math.max(1, Math.ceil(checklistsCount / recordsPerPage));
  const pageIndex = Math.min(pagination.page, totalPages - 1);

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <>
      <section>
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Checklists</h1>
        </div>

        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div>
              {canManageChecklists && (
                <button
                  className="uk-button hms-btn hms-red-btn"
                  uk-toggle="target: #checklist-modal"
                  onClick={handleAdd}
                >
                  <span uk-icon="icon: plus; ratio: 0.7"></span> Checklist
                </button>
              )}
            </div>
          </div>

          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              <Flex alignment="between top" className="guest-form-top-filters">
                <form
                  className="uk-form uk-flex uk-flex-middle hms-form"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <div className="uk-inline">
                    <div className="uk-search uk-search-default uk-form-custom uk-margin-small-right">
                      {criteria.search_term ? (
                        <a
                          className="uk-form-icon-flip uk-form-icon"
                          uk-icon="icon: close"
                          onClick={() => handleCriteriaChange("search_term", "")}
                        ></a>
                      ) : (
                        <span className="uk-search-icon-flip" uk-search-icon="true" />
                      )}
                      <input
                        className="uk-search-input"
                        type="search"
                        placeholder="Checklist Name"
                        value={criteria.search_term}
                        onChange={(e) => handleCriteriaChange("search_term", e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Type filter (replaces Scope) */}
                  <div className="uk-margin-small-right" style={{ minWidth: 220 }}>
                    <div uk-form-custom="target: > * > span:first-child">
                      <select
                        value={criteria.type}
                        onChange={(e) => handleCriteriaChange("type", e.target.value)}
                      >
                        {TASK_CATEGORY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>

                      <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                        <span className="select-value">
                          {labelForTypeValue(criteria.type)}
                        </span>
                        <span uk-icon="icon: chevron-down" className="hms-align-right"></span>
                      </button>
                    </div>
                  </div>
                </form>
              </Flex>

              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th className="uk-table-expand">Name</th>
                    <th>Type</th>
                    <th># Items</th>
                    <th className="actions">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan={4} className="uk-text-center">
                        <div uk-spinner="ratio: 1"></div>
                      </td>
                    </tr>
                  )}

                  {!loading && error && (
                    <tr>
                      <td colSpan={4}>
                        <div className="uk-alert-danger" uk-alert="">
                          <p>{error}</p>
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading && !error && checklists.length === 0 && (
                    <tr>
                      <td colSpan={4}>
                        <div className="uk-text-center uk-padding-small uk-text-meta">
                          {criteria.search_term || criteria.type !== "all"
                            ? "No checklists match your filters."
                            : "No checklists found. Create your first checklist to get started."}
                        </div>
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    !error &&
                    checklists.map((checklist) => (
                      <tr key={checklist.id}>
                        <td className="semi-bold-font">{checklist.name}</td>
                        <td>{labelForTypeValue(checklist.type)}</td>
                        <td>{checklist.item_count || 0}</td>
                        <td className="hms-action-btn">
                          <div className="uk-flex uk-flex-top">
                            {canManageChecklists && (
                              <div className="uk-inline">
                                <button
                                  className="uk-button"
                                  uk-toggle="target: #checklist-modal"
                                  onClick={() => handleEdit(checklist)}
                                  uk-tooltip="Edit"
                                >
                                  <Image src="/images/edit.svg" />
                                </button>
                              </div>
                            )}

                            {canManageChecklists && (
                              <div className="uk-inline">
                                <button
                                  className="uk-button"
                                  onClick={() => handleClone(checklist)}
                                  uk-tooltip="Clone"
                                  type="button"
                                >
                                  <Image src="/images/clone.svg" />
                                </button>
                              </div>
                            )}

                            {canManageChecklists && (
                              <div className="uk-inline">
                                <button
                                  className="uk-button"
                                  onClick={() => handleDelete(checklist)}
                                  uk-toggle="target: #modal-checklist-delete"
                                  uk-tooltip="Delete"
                                  type="button"
                                >
                                  <Image src="/images/delete.svg" />
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>

          <Flex alignment="between top" className="guest-form-top-filters">
            <Pagination 
              count={checklistsCount} 
              perPage={recordsPerPage}
              index={pageIndex} 
              onIndexChanged={handleIndexChanged} 
            />
            <RecordsPerPage />
          </Flex>
        </div>
      </section>

      {/* Modal/offcanvas */}
      <ChecklistAdd template={selectedTemplate} addRequestKey={addRequestKey} />
      
      {/* Delete confirmation dialog */}
      <DeleteDialog
        id="modal-checklist-delete"
        title="Delete Checklist"
        itemName={checklistToDelete?.name}
        item={checklistToDelete}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default Checklists;
